import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AccountCategoryResponse,
  AccountManagementAuthenticationModel,
  AccountManagementClientApiModel,
  AccountManagementInvitationModel,
  AccountManagementUserModel,
  ModifyUserRoleModel,
  UserToAddModel,
} from 'src/app/models/api/accountManagement.model';
import { HttpService } from '../http/http.service';

@Injectable({
  providedIn: 'root',
})
export class AccountManagementHttpService {
  public readonly defaultPageSize: number = 50;

  /**
   * The user management base url
   * @private
   * @readonly
   */
  private readonly _baseUrl: string = 'account';

  constructor(private readonly _httpService: HttpService) {}

  /**
   * Get all users
   * @returns A collection of AccountManagementUserModel
   */
  public getUsers(): Observable<AccountManagementUserModel[]> {
    const url = `${this._baseUrl}/users`;
    return this._httpService.get<AccountManagementUserModel[]>(url);
  }

  /**
   * Get all invitations
   * @returns A collection of AccountManagementUserModel
   */
  public getInvitations(): Observable<AccountManagementInvitationModel[]> {
    const url = `${this._baseUrl}/invitations`;
    return this._httpService.get<AccountManagementInvitationModel[]>(url);
  }

  /**
   * Get all authentications
   * @returns A collection of AccountManagementUserModel
   */
  public getAuthentications(): Observable<AccountManagementAuthenticationModel[]> {
    const url = `${this._baseUrl}/authentications`;
    return this._httpService.get<AccountManagementAuthenticationModel[]>(url);
  }

  /**
   * Invite a user
   * @returns invitation id
   */
  public inviteUser(userToAdd: UserToAddModel): Observable<string> {
    const url = `${this._baseUrl}/users/invite`;
    return this._httpService.post<UserToAddModel, string>(url, userToAdd);
  }

  /**
   * Deletes a user
   */
  public deleteUsers(ids: string[]): Observable<string> {
    const url = `${this._baseUrl}/delete-users`;
    return this._httpService.post<string[], string>(url, ids);
  }

  /**
   * Deletes an invitation
   */
  public deleteInvitation(id: string): Observable<string> {
    const url = `${this._baseUrl}/invitations/${id}`;
    return this._httpService.delete<string>(url);
  }

  /**
   * Modify user role
   */
  public modifyRole(userToChange: ModifyUserRoleModel): Observable<string> {
    const url = `${this._baseUrl}/users/roles`;
    return this._httpService.put<string, ModifyUserRoleModel>(url, userToChange);
  }

  /**
   * Get current account api details
   * @returns An instance of AccountManagementClientApiModel
   */
  public apiDetails(): Observable<AccountManagementClientApiModel> {
    const url = `${this._baseUrl}/api`;
    return this._httpService.get<AccountManagementClientApiModel>(url);
  }

  /**
   * Rotate client secret
   * @returns An instance of AccountManagementClientApiModel
   */
  public rotateClientSecret(): Observable<string> {
    const url = `${this._baseUrl}/api/rotate-secret`;
    return this._httpService.post<string, string>(url, '');
  }

  public getAccountDetails$(): Observable<AccountCategoryResponse> {
    const url = `${this._baseUrl}/details`;
    return this._httpService.get<AccountCategoryResponse>(url);
  }
}
